import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { AuditLogController_list } from '@/services/services';

import { mapAuditLogEntry } from './mappers';
import { toAuditLogQueryParams } from './queryParams';
import type { AuditLogEntry, AuditLogFilters, UseAuditLogResult } from './types';

const PAGE_SIZE = 50;
/** The first page has no cursor. */
const FIRST_PAGE: string | undefined = undefined;

export function useAuditLog(filters: AuditLogFilters): UseAuditLogResult {
    const params = toAuditLogQueryParams(filters);
    const query = useInfiniteQuery({
        queryKey: ['auditLog', params],
        queryFn: async ({ pageParam }) =>
            (await AuditLogController_list({ ...params, cursor: pageParam, limit: PAGE_SIZE }))
                .data,
        initialPageParam: FIRST_PAGE,
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
        staleTime: 30_000,
        // The page is where an investigation starts: coming back to it shows what has been
        // written since, not the pages cached when it was last open.
        refetchOnMount: 'always',
    });

    const entries = useMemo<AuditLogEntry[]>(
        () => query.data?.pages.flatMap((page) => page.entries.map(mapAuditLogEntry)) ?? [],
        [query.data]
    );

    return {
        entries,
        hasNextPage: query.hasNextPage ?? false,
        fetchNextPage: query.fetchNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        isLoading: query.isPending,
        error: query.error,
        refetch: query.refetch,
        // A refresh refetches every loaded page; loading the next one is not a refresh.
        isRefreshing: query.isRefetching && !query.isFetchingNextPage,
    };
}
